import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { isEmpty } from "lodash-es";

import PageTabContent from "@/App/Layouts/PageBody/PageTabContent";
import PageTabHeader from "@/App/Layouts/PageBody/PageTabHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import Datetime from "@/common/components/Datetime/Datetime";
import FoldableBlock from "@/common/components/Display/FoldableBlock";
import { VisualModelViewerOfVehicleDamage } from "@/common/components/Entity/VehicleVisualModel/Viewer/v2/Entity/VisualModelViewerOfVehicleDamage";
import EntityChangedByInfoCard from "@/common/components/EntityInfo/EntityChangedByInfoCard";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import FileListView from "@/common/components/Files/FileListView";
import FieldValue from "@/common/components/Form/Display/FieldValue";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppPopover from "@/common/components/Popover/AppPopover";
import AppPopoverContent from "@/common/components/Popover/AppPopoverContent";
import AppTypography from "@/common/components/Text/AppTypography";
import { ATTACHMENTS_COMMON_ITEM_SIZES } from "@/common/constants/common";
import { FileItem } from "@/common/fileItem";
import { TypeHelper } from "@/common/helpers/type";
import { useEntityFormMeta } from "@/common/hooks/entity/useEntityFormMeta";
import { VehicleDamageDtoAll, VehicleDamageDtoAny } from "@/common/mapping/mappers/vehicleDamage";
import { ApiEnumName, enumService } from "@/common/services/enum";
import { VehicleDamageState } from "@/core/api/generated/v1";

import ContractLink from "../../../Contract/ContractLink";
import DamageCostEvaluationLink from "../../../DamageCostEvaluation/DamageCostEvaluationLink";
import DamageDetectionLink from "../../../DamageDetection/DamageDetectionLink";
import GeneralByWhoDisplay from "../../../General/Display/GeneralByWhoDisplay";
import GeneralInspectorDisplay from "../../../General/Display/GeneralInspectorDisplay";
import GeneralStrictEntityRelationLink from "../../../General/Display/GeneralStrictEntityRelationLink";
import RepairOperationLink from "../../../RepairOperation/RepairOperationLink";
import VehicleDamageCorrectionDisplay from "../../VehicleDamageCorrectionDisplay";
import VehicleDamageLink from "../../VehicleDamageLink";
import { VehicleDamageStatus } from "../../VehicleDamageStatus";
import { VehicleDamageViewProps } from "../VehicleDamageView";

interface Props {
  entity: VehicleDamageDtoAny;
  displayProps: VehicleDamageViewProps["displayProps"];
}

export default function OverviewTabContent({ entity, displayProps }: Props) {
  const entityAll = entity as VehicleDamageDtoAll;
  // const entityData = entity as VehicleDamageDataDtoAll;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const entityFormMeta = useEntityFormMeta({ entity });

  return (
    <PageTabContent>
      <ViewContentBlock>
        <PageTabHeader
          title={"Details"}
          subtitle2={<EntityChangedByInfoCard entity={entityAll} />}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xxs: "1fr",
              lg: "2fr 2fr",
            },
            columnGap: 2,
            rowGap: 2,
          }}
        >
          <Stack spacing={1}>
            {entityFormMeta?.isEntity && entityAll.localNumber && (
              <FieldValue label='Local number' isEmpty={!entityAll.localNumber}>
                {entityAll.localNumber}
              </FieldValue>
            )}

            <FieldValue label='Part type' isEmpty={!entityAll.partType && !entityAll.partTypeLocal}>
              {entityAll.partType?.name || entityAll.partTypeLocal?.name}
            </FieldValue>

            <FieldValue
              label='Damage type'
              isEmpty={!entityAll.damageType && !entityAll.damageTypeLocal}
            >
              {entityAll.damageType?.name || entityAll.damageTypeLocal?.name}
            </FieldValue>

            <FieldValue label='Area' isEmpty={!entityAll.area}>
              {entityAll.area}
            </FieldValue>

            <FieldValue label='Projection' isEmpty={!entityAll.projection}>
              {entityAll.projection}
            </FieldValue>

            {entityAll.state && (
              <FieldValue label='State' isEmpty={!entityAll.state}>
                <VehicleDamageStatus
                  vehicleDamage={entityAll}
                  inlineApiEnumValueProps={{ withHelperTooltip: false, withDescription: false }}
                />

                {(entityAll.state === VehicleDamageState.Inactive ||
                  entityAll.state === VehicleDamageState.Repaired) && (
                  <AppPopover
                    hoverBehavior={{ closeBehavior: "onTriggerOrContentMouseLeave" }}
                    trigger={<AppIcon sx={{ ml: 0.5 }} of='info' inText />}
                  >
                    <AppPopoverContent>
                      <Stack gap={1}>
                        <FieldValue label='Description'>
                          {enumService.getEnumValueDescription(
                            ApiEnumName.VehicleDamageState,
                            entityAll.state,
                          ) || "-"}
                        </FieldValue>

                        {entityAll.state === VehicleDamageState.Inactive && (
                          <FieldValue label='Inactive reason'>
                            <InlineApiEnumValue
                              type={ApiEnumName.VehicleDamageInactiveReasonType}
                              value={entityAll.inactiveReasonType}
                              withHelperTooltip
                            />
                          </FieldValue>
                        )}

                        {entityAll.state === VehicleDamageState.Repaired && (
                          <>
                            <FieldValue
                              label='Repaired at'
                              direction={isMobile ? "column" : "row"}
                              isEmpty={!entityAll.repairedAt}
                            >
                              <Datetime datetime={entityAll.repairedAt} withDurationFromNow />
                            </FieldValue>

                            <FieldValue label='Repaired by' isEmpty={!entityAll.repairedBy}>
                              <GeneralByWhoDisplay who={entityAll.repairedBy} />
                            </FieldValue>

                            {displayProps?.repairOperation && entityAll.repairOperation && (
                              <FieldValue label='Repaired in' isEmpty={!entityAll.repairOperation}>
                                <RepairOperationLink entity={entityAll.repairOperation} />
                              </FieldValue>
                            )}
                          </>
                        )}

                        {entityAll.state === VehicleDamageState.Inactive && (
                          <>
                            <FieldValue
                              label='Inactive at'
                              direction={isMobile ? "column" : "row"}
                              isEmpty={!entityAll.inactiveAt}
                            >
                              <Datetime datetime={entityAll.inactiveAt} withDurationFromNow />
                            </FieldValue>

                            <FieldValue label='Inactive by' isEmpty={!entityAll.inactiveBy}>
                              <GeneralByWhoDisplay who={entityAll.inactiveBy} />
                            </FieldValue>

                            {entityAll.inactiveIn && (
                              <FieldValue label='Inactive in' isEmpty={!entityAll.inactiveIn}>
                                <GeneralStrictEntityRelationLink value={entityAll.inactiveIn} />
                              </FieldValue>
                            )}
                          </>
                        )}
                      </Stack>
                    </AppPopoverContent>
                  </AppPopover>
                )}
              </FieldValue>
            )}

            {entityAll.correctionStatus && (
              <FieldValue label='Correction status' isEmpty={!entityAll.correctionStatus}>
                <InlineApiEnumValue
                  type={ApiEnumName.DamageCorrectionStatus}
                  value={entityAll.correctionStatus}
                  withDescription={false}
                  withHelperTooltip={false}
                />

                {entityAll.correctionStatus && (
                  <AppPopover
                    hoverBehavior={{ closeBehavior: "onTriggerOrContentMouseLeave" }}
                    trigger={<AppIcon sx={{ ml: 0.5 }} of='info' inText />}
                  >
                    <AppPopoverContent>
                      <Stack gap={1}>
                        <FieldValue label='Description'>
                          {enumService.getEnumValueDescription(
                            ApiEnumName.DamageCorrectionStatus,
                            entityAll.correctionStatus,
                          ) || "-"}
                        </FieldValue>

                        <FieldValue
                          label='Corrected at'
                          direction={isMobile ? "column" : "row"}
                          isEmpty={!entityAll.correctedAt}
                        >
                          <Datetime datetime={entityAll.correctedAt} withDurationFromNow />
                        </FieldValue>

                        <FieldValue label='Corrected by' isEmpty={!entityAll.correctedBy}>
                          <GeneralByWhoDisplay who={entityAll.correctedBy} />
                        </FieldValue>

                        {entityAll.correctedIn && (
                          <FieldValue label='Corrected in' isEmpty={!entityAll.correctedIn}>
                            <GeneralStrictEntityRelationLink value={entityAll.correctedIn} />
                          </FieldValue>
                        )}
                      </Stack>
                    </AppPopoverContent>
                  </AppPopover>
                )}
              </FieldValue>
            )}

            {entityAll.inspectedAt && (
              <FieldValue
                label='Inspection date'
                direction={isMobile ? "column" : "row"}
                isEmpty={!entityAll.inspectedAt}
              >
                <Datetime datetime={entityAll.inspectedAt} withDurationFromNow />
              </FieldValue>
            )}

            {displayProps?.inspector && entityAll.inspector && (
              <FieldValue
                label='Inspector'
                direction={isMobile ? "column" : "row"}
                isEmpty={!entityAll.inspector}
              >
                <GeneralInspectorDisplay
                  inspector={entityAll.inspector}
                  inlineUserProps={{
                    withAvatar: true,
                    withLink: true,
                  }}
                />
              </FieldValue>
            )}

            {displayProps?.contract && (
              <FieldValue label='Contract' isEmpty={!entityAll.contract}>
                <ContractLink entity={entityAll.contract} />
              </FieldValue>
            )}

            {displayProps?.damageDetection && entityAll.damageDetection && (
              <FieldValue label='Damage detection' isEmpty={!entityAll.damageDetection}>
                <DamageDetectionLink entity={entityAll.damageDetection} />
              </FieldValue>
            )}

            {displayProps?.damageCostEvaluation && entityAll.damageCostEvaluation && (
              <FieldValue label='Damage cost evaluation' isEmpty={!entityAll.damageCostEvaluation}>
                <DamageCostEvaluationLink entity={entityAll.damageCostEvaluation} />
              </FieldValue>
            )}

            {displayProps?.repairOperation && entityAll.repairOperation && (
              <FieldValue label='Repair operation' isEmpty={!entityAll.repairOperation}>
                <RepairOperationLink entity={entityAll.repairOperation} />
              </FieldValue>
            )}

            {/* {displayProps?.originalDamage && entityAll.originalId && (
              <FieldValue label='Original damage' isEmpty={!entityAll.originalId}>
                <VehicleDamageLink
                  vehicleId={undefined}
                  entity={undefined}
                  entityId={entityAll.originalId}
                  inlineProps={{
                    withMenu: true,
                  }}
                />
              </FieldValue>
            )} */}

            {displayProps?.originalDamage &&
              TypeHelper.isNotEmptyArray(entityAll.originalIdsAll) && (
                <FieldValue
                  label='Original damages'
                  isEmpty={TypeHelper.isEmptyArray(entityAll.originalIdsAll)}
                >
                  <Stack>
                    {entityAll.originalIdsAll?.map((originalId) => (
                      <VehicleDamageLink
                        key={originalId}
                        vehicleId={undefined}
                        entity={undefined}
                        entityId={originalId}
                        inlineProps={{
                          withMenu: true,
                        }}
                      />
                    ))}
                  </Stack>
                </FieldValue>
              )}

            {displayProps?.originalDamage && entityAll.originalDamageDetectionId && (
              <FieldValue
                label='Original damage detection'
                isEmpty={!entityAll.originalDamageDetectionId}
              >
                <DamageDetectionLink
                  entityId={entityAll.originalDamageDetectionId}
                  entity={undefined}
                />
              </FieldValue>
            )}

            {entityAll.notes && (
              <FieldValue label='Notes' isEmpty={!entityAll.notes}>
                {entityAll.notes}
              </FieldValue>
            )}

            {!isEmpty(entityAll.attachments) && (
              <Box sx={{ mt: 2 }}>
                <FileListView
                  files={FileItem.createManyFrom(entityAll.attachments)}
                  maxVisibleFiles={10}
                  itemWidth={ATTACHMENTS_COMMON_ITEM_SIZES.WIDTH}
                  itemHeight={ATTACHMENTS_COMMON_ITEM_SIZES.HEIGHT}
                />
              </Box>
            )}
          </Stack>

          {/* Visual model & damage point */}
          {displayProps?.visualModel && (
            <Stack spacing={1} sx={{ overflow: "hidden" }}>
              <VisualModelViewerOfVehicleDamage entity={entityAll} />

              {/* Old visual model */}
              {entityAll.oldVisualModel && (
                <FoldableBlock
                  defaultIsFolded
                  trigger={{
                    label: <Typography variant='subtitle1'>Old visual model</Typography>,
                  }}
                >
                  <Box>
                    <VisualModelViewerOfVehicleDamage entity={entityAll} isDisplayOldVisualModel />
                  </Box>
                </FoldableBlock>
              )}
            </Stack>
          )}
        </Box>
      </ViewContentBlock>

      {entityAll.sourceCorrection && (
        <ViewContentBlock>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xxs: "1fr",
                lg: "2fr 2fr",
              },
              columnGap: 2,
              rowGap: 2,
            }}
          >
            <Stack gap={1}>
              <AppTypography
                sx={{ width: "fit-content" }}
                variant='h6'
                decoration={{ variant: "helpText" }}
                tooltipProps={{
                  title:
                    "Source correction where original damage was corrected and this damage was created from it",
                }}
              >
                Source correction
              </AppTypography>

              <VehicleDamageCorrectionDisplay correction={entityAll.sourceCorrection} />
            </Stack>
          </Box>
        </ViewContentBlock>
      )}

      {entityAll.targetCorrection && (
        <ViewContentBlock>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xxs: "1fr",
                lg: "2fr 2fr",
              },
              columnGap: 2,
              rowGap: 2,
            }}
          >
            <Stack gap={1}>
              <AppTypography
                sx={{ width: "fit-content" }}
                variant='h6'
                decoration={{ variant: "helpText" }}
                tooltipProps={{
                  title:
                    "Target correction where this damage was corrected and a new damage was created from it",
                }}
              >
                Target correction
              </AppTypography>

              <VehicleDamageCorrectionDisplay correction={entityAll.targetCorrection} />
            </Stack>
          </Box>
        </ViewContentBlock>
      )}
    </PageTabContent>
  );
}
